import { useEffect, useState, ReactElement } from 'react';
import RadarChart from 'components/RadarChart';
import {
  StyledProduct,
  ProductDotColor,
  StyledCaptionsContainer,
  StyledCol,
  StyledAcronym,
  StyledCaption,
} from '../ProductComparison/ProductComparison.styles';
import { useTranslation } from 'react-i18next';
import { Row } from 'syngenta-digital-cropwise-react-ui-kit';
import { useAppState } from 'context/AppState';
import { useBreakpoint } from 'hooks';
import {
  ChartContainer,
  ProductComparisonContent,
  ProductComparisonTitle,
  ProductList,
  SectionContainer,
  Text,
} from 'pages/RecommendationV2/V2Common.styles';
import brazilSpiderChart from '../../../../brazil-spider-chart.json';

// Attribute display labels from the Spider Chart Excel (in desired chart order)
const SPIDER_ATTRIBUTES = [
  'Stem Quality',
  'Husk',
  'Corn Stunt Tolerance',
  'Bipolaris Tolerance',
  'Bacterial leaf streak Tolerance',
  'Helminthosporium Tolerance',
  'Diploidia Tolerance',
  'Cercosporiose Tolerance',
  'White Spot Tolerance',
] as const;

// Preserve catalog keys for scores and flags; use the existing Phrase keys for display.
const ATTRIBUTE_TRANSLATION_KEYS: Partial<Record<(typeof SPIDER_ATTRIBUTES)[number], string>> = {
  'Bacterial leaf streak Tolerance': 'Bacterial Leaf Streak Tolerance',
  'Diploidia Tolerance': 'Diplodia Tolerance',
  'Cercosporiose Tolerance': 'Cercospora Tolerance',
};

const MAX_SCORE = 5;

interface IAttribute {
  [key: string]: string;
}

interface SpiderProduct {
  productKey: string;
  color: string;
  commercialName: string;
  spiderData: Record<string, number | null>;
}

interface ChartDataPoint {
  data: Record<string, number>;
  meta: { color: string; strokeWidth: number };
}

export function BrazilProductComparison({
  sectionRef,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly sectionRef?: any;
}): ReactElement | null {
  const { t } = useTranslation();
  const {
    apiData: { recommendationCropwise },
  } = useAppState();
  const screens = useBreakpoint();
  const { isMobile, isTablet, portrait, landscape } = screens;
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [captions, setCaptions] = useState<IAttribute | null>(null);
  const [mobileCaptions, setMobileCaptions] = useState<IAttribute | null>(null);
  const [productList, setProductList] = useState<SpiderProduct[]>([]);
  const [hoveredProductColor, setHoveredProductColor] = useState<string | undefined>(undefined);

  const options = {
    zoomDistance: 1.78,
    dots: true,
    axes: true,
    axesType: 'line',
    scales: 5,
    captions: true,
    captionLineHeight: 49,
    captionProps: (col: { angle: number }) => {
      const degrees = (col.angle * 180) / Math.PI;
      let textAnchor = degrees === 0 || degrees === 180 ? 'middle' : 'start';
      if (degrees > 180) {
        textAnchor = 'end';
      }
      return {
        fontSize: isTablet ? 13 : 16,
        className: 'caption',
        margin: isMobile ? 26 : 38,
        textAnchor,
      };
    },
    dotProps: (meta: { color: string }) => ({
      r: 6,
      fill: meta.color,
    }),
    hoveredProductColor,
    setHoveredProductColor,
    isMobile,
  };

  useEffect(() => {
    const products = recommendationCropwise?.recommendations?.[0]?.products ?? [];

    const matched: SpiderProduct[] = products
      .filter((p) => p.product_name !== 'None')
      .map((p) => {
        const productKey = (p.commercial_name ?? p.product_name).replace(/\s/g, '');
        const entry = (brazilSpiderChart as Array<{ product: string; [key: string]: string | number | null }>).find(
          (e) => e.product.toLowerCase() === productKey.toLowerCase()
        );
        if (!entry) {
          return null;
        }
        return {
          productKey,
          color: p.color,
          commercialName: p.commercial_name ?? productKey,
          spiderData: entry as Record<string, number | null>,
        };
      })
      .filter(Boolean) as SpiderProduct[];

    setProductList(matched);

    const brazilAgronomicAttributes =
      recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.agronomicInputs
        ?.brazil_agronomic_attributes;

    // Only use attributes flagged as available for this recommendation (API-driven),
    // falling back to showing all when the API doesn't send the flag, or sends it empty
    const hasBrazilAgronomicAttributes =
      brazilAgronomicAttributes && Object.keys(brazilAgronomicAttributes).length > 0;
    const enabledAttributes = hasBrazilAgronomicAttributes
      ? SPIDER_ATTRIBUTES.filter((attr) => brazilAgronomicAttributes[attr] === true)
      : SPIDER_ATTRIBUTES;

    // Only use attributes where ALL matched products have non-null data
    const validAttributes = enabledAttributes.filter((attr) =>
      matched.every(
        (p) => p.spiderData[attr] !== null && p.spiderData[attr] !== undefined
      )
    );

    if (validAttributes.length === 0 || matched.length === 0) {
      setCaptions(null);
      setMobileCaptions(null);
      setChartData([]);
      return;
    }

    const captionsObj: IAttribute = {};
    validAttributes.forEach((attr) => {
      captionsObj[attr] = t(ATTRIBUTE_TRANSLATION_KEYS[attr] ?? attr);
    });
    setCaptions(captionsObj);

    // Build mobile abbreviation captions
    if (isMobile) {
      const usedValues: string[] = [];
      const mobileCaptionsObj: IAttribute = {};
      validAttributes.forEach((attr) => {
        let shortName = attr
          .split(/\s/)
          .map((word) => word.slice(0, 1))
          .join('')
          .slice(0, 3);
        if (usedValues.includes(shortName)) {
          const count = usedValues.filter((v) => v === shortName).length;
          usedValues.push(shortName);
          shortName = `${shortName}${count}`;
        } else {
          usedValues.push(shortName);
        }
        mobileCaptionsObj[attr] = shortName;
      });
      setMobileCaptions(mobileCaptionsObj);
    }

    // Build chart data: normalize from 1-5 scale to 0-1 for RadarChart
    const newData = matched.map((p) => ({
      data: validAttributes.reduce<Record<string, number>>((obj, attr) => {
        const rawVal = p.spiderData[attr];
        const normalized =
          typeof rawVal === 'number' ? Math.min(rawVal / MAX_SCORE, 1) : 0;
        return { ...obj, [attr]: normalized };
      }, {}),
      meta: {
        color: p.color,
        strokeWidth: 4,
      },
    }));

    setChartData(newData);
  }, [recommendationCropwise, isMobile, t]);

  const calculateSize = () => {
    let size = 950;
    if (isMobile) {
      size = window.screen.width;
    } else if (isTablet) {
      if (portrait) {
        size = window.screen.width - 110;
      } else if (landscape) {
        size = 615;
      }
    }
    return size;
  };

  if (!captions || Object.keys(captions).length === 0 || chartData.length === 0) {
    return null;
  }

  return (
    <SectionContainer data-testid="brazil-product-comparison-section" ref={sectionRef}>
      <ProductComparisonContent>
        <ProductComparisonTitle>{t('Product Comparison')}</ProductComparisonTitle>
        <Text>{t('The chart below shows the agronomic profile of each product for an easy comparison.')}</Text>
        <ChartContainer>
          {(captions || mobileCaptions) && (
            <RadarChart
              captions={isMobile ? mobileCaptions : captions}
              data={chartData}
              size={calculateSize()}
              options={options}
            />
          )}
        </ChartContainer>
        <ProductList>
          {productList.map((p) => (
            <StyledProduct
              key={p.productKey}
              onMouseEnter={() => setHoveredProductColor(p.color)}
              onMouseLeave={() => setHoveredProductColor(undefined)}
            >
              <ProductDotColor color={p.color ?? ''} />
              {p.commercialName}
            </StyledProduct>
          ))}
        </ProductList>
        {isMobile && captions && mobileCaptions && (
          <StyledCaptionsContainer>
            <Row>
              {Object.keys(captions).map((key) => (
                <StyledCol key={key} span={12}>
                  <StyledAcronym>{mobileCaptions[key]}</StyledAcronym>
                  <StyledCaption>{captions[key]}</StyledCaption>
                </StyledCol>
              ))}
            </Row>
          </StyledCaptionsContainer>
        )}
      </ProductComparisonContent>
    </SectionContainer>
  );
}
